import { Locales, getField, Utils } from "@simplyk/common";
import { v4 } from "uuid";

import { QuestionFormData, QuestionFieldFormData, ChoiceFormData } from "@/common/types/customQuestion";
import { ChoiceOutput, QuestionOutput, RateOutput } from "@/common/types/trpc";

type NewChoiceFieldInput = {
  id: string;
  locale: Locales;
  body: string;
  choiceId: string;
};

type NewQuestionFieldInput = {
  id: string;
  locale: Locales;
  body: string;
  questionId: string;
};

export const mapGraphQLDataToProvideQuestions = (
  questions: QuestionOutput[] | null | undefined,
  locale: Locales,
  rates?: RateOutput[] | null
): QuestionFormData[] => {
  if (!questions || questions.length === 0) {
    return [];
  }
  return questions
    .slice()
    .sort((questionA, questionB) => questionA.sortIndex - questionB.sortIndex)
    .map((question) => {
      const questionField = getField(question.questionFields ?? [], locale);
      const ratesMap = rates && Utils.arrayToIdMap(rates);

      const isAssignedToSpecifiedRates = !rates?.every((rate) =>
        (question?.rateQuestions ?? []).some((rateQuestion) => rateQuestion.rateId === rate.id)
      );
      const ratesAssigned = question?.rateQuestions
        ?.map((rateQuestion) => ({
          value: rateQuestion.rateId,
          label:
            (ratesMap?.[rateQuestion.rateId]?.rateFields &&
              getField(ratesMap?.[rateQuestion.rateId]?.rateFields, locale)?.title) ||
            "",
        }))
        .filter((rateQuestion) => rates?.some((rate) => rate.id === rateQuestion.value));

      return {
        id: question.id,
        body: questionField?.body || "",
        required: question.required,
        type: question.type,
        countAlreadyAnswered: question.countAlreadyAnswered,
        isMaximumActive: (question.choices ?? []).some((choice) => choice.maximum && choice.maximum > 0),
        isAssignedToSpecifiedRates,
        askForAllRates: !isAssignedToSpecifiedRates,
        ratesAssigned,
        choices:
          (question.choices ?? [])
            .slice()
            .sort((choiceA, choiceB) => choiceA.sortIndex - choiceB.sortIndex)
            .map((choice) => {
              return {
                id: choice.id,
                value: getField(choice.choiceFields, locale)?.body || "",
                maximum: choice.maximum ?? undefined,
                sortIndex: choice.sortIndex,
                remainingCapacity: choice.remainingCapacity ?? undefined,
              };
            }) || [],
        questionFieldId: questionField?.id || "",
        scope: question.hasRate ? "participant" : "buyer",
      };
    });
};

export const mapDataToProvideQuestions = (
  questions: QuestionOutput[] | null | undefined,
  locale: Locales,
  rates?: RateOutput[] | null
): QuestionFormData[] => {
  if (!questions || questions.length === 0) {
    return [];
  }
  return questions
    .slice()
    .sort((questionA, questionB) => questionA.sortIndex - questionB.sortIndex)
    .map((question) => {
      const questionField = getField(question.questionFields ?? [], locale);
      const ratesMap = rates && Utils.arrayToIdMap(rates);

      const isAssignedToSpecifiedRates = !rates?.every((rate) =>
        (question?.rateQuestions ?? []).some((rateQuestion) => rateQuestion.rateId === rate.id)
      );
      const ratesAssigned = question?.rateQuestions
        ?.map((rateQuestion) => ({
          value: rateQuestion.rateId,
          label:
            (ratesMap?.[rateQuestion.rateId]?.rateFields &&
              getField(ratesMap?.[rateQuestion.rateId]?.rateFields, locale)?.title) ||
            "",
        }))
        .filter((rateQuestion) => rates?.some((rate) => rate.id === rateQuestion.value));

      return {
        id: question.id,
        body: questionField?.body || "",
        required: question.required,
        type: question.type,
        countAlreadyAnswered: question.countAlreadyAnswered,
        isMaximumActive: question.choices?.some((choice) => choice.maximum && choice.maximum > 0) || false,
        isAssignedToSpecifiedRates,
        askForAllRates: !isAssignedToSpecifiedRates,
        ratesAssigned,
        choices:
          (question.choices ?? [])
            .slice()
            .sort((choiceA, choiceB) => choiceA.sortIndex - choiceB.sortIndex)
            .map((choice) => {
              return {
                id: choice.id,
                value: getField(choice.choiceFields, locale)?.body || "",
                maximum: choice.maximum ?? undefined,
                sortIndex: choice.sortIndex,
                remainingCapacity: choice.remainingCapacity ?? undefined,
              };
            }) || [],
        questionFieldId: questionField?.id || "",
      };
    });
};

export const mapDataToEditQuestionFields = (
  fields: QuestionFieldFormData[] = [],
  locale: Locales
): NewQuestionFieldInput[] =>
  fields.map((field) => ({
    id: field.id,
    body: field.body,
    questionId: field.questionId,
    locale,
  }));

export const mapGraphQLDataToEditChoiceFields = (
  choices: ChoiceFormData[] = [],
  existingChoices: ChoiceOutput[],
  locale: Locales
): NewChoiceFieldInput[] =>
  choices.map((choice) => {
    const existingChoice = existingChoices.find((existingChoice) => existingChoice.id === choice.id);
    const existingChoiceField =
      existingChoice?.choiceFields && existingChoice?.choiceFields.find((choiceField) => choiceField.locale === locale);
    return {
      id: existingChoiceField?.id || v4(),
      choiceId: existingChoice?.id || v4(),
      body: choice.value,
      locale,
    };
  });

export const mapDataToEditChoiceFields = (
  choices: ChoiceFormData[] = [],
  existingChoices: ChoiceOutput[],
  locale: Locales
): NewChoiceFieldInput[] =>
  choices.map((choice) => {
    const existingChoice = existingChoices.find((existingChoice) => existingChoice.id === choice.id);
    const existingChoiceField =
      existingChoice?.choiceFields && existingChoice?.choiceFields.find((choiceField) => choiceField.locale === locale);
    return {
      id: existingChoiceField?.id || v4(),
      choiceId: existingChoice?.id || v4(),
      body: choice.value,
      locale,
    };
  });
